import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { MaritalStatus } from '../../../../../shared/models/enums/marital-status.enum';
import { TaxSituation } from '../../../../../shared/models/enums/tax-situation.enum';
import { BeneficiaryDto } from '../../../../dto/beneficiary.dto';
import { RsjInstructionPersonalDataDto } from '../../dto/rsj-instruction-personal-data.dto';
import { Utils } from '../../../../../shared/utils';

@Component({
  selector: 'app-rsj-instruction-personal-data',
  templateUrl: './rsj-instruction-personal-data.component.html',
})
export class RsjInstructionPersonalDataComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public personalDataForm: UntypedFormGroup;
  public updated: boolean = false;
  public submitted: boolean = false;

  public readonly MaritalStatus = MaritalStatus;

  public showPartnerOnRsa: boolean = false;
  public taxSituationCase: 1 | 2 | 3;

  public maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2;
  public maxFileSizeStr: string = Utils.humanFileSize(this.maxFileSize, true, 2);

  @Input() public rsjPersonalData: RsjInstructionPersonalDataDto;
  @Input() public beneficiary: BeneficiaryDto;
  @Input() public disabled: boolean;

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.initForm();
    this.initValueChangeHandlers();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.personalDataForm.valid;
  }

  public getDto(): RsjInstructionPersonalDataDto {
    if (this.personalDataForm.valid) {
      this.rsjPersonalData = {
        underSupervision: this.personalDataForm.controls.underSupervision.value,
        supervisionDocument1: this.personalDataForm.controls.supervisionDocument1.value,
        supervisionDocument2: this.personalDataForm.controls.supervisionDocument2.value,
        maritalStatus: this.personalDataForm.controls.maritalStatus.value,
        partnerOnRsa: this.personalDataForm.controls.partnerOnRsa.value,
        dependentsNumber: this.personalDataForm.controls.dependentsNumber.value,
        pregnancy: this.personalDataForm.controls.pregnancy.value,
        rsaSimulation: this.personalDataForm.controls.rsaSimulation.value,
        rsaSimulationComment: this.personalDataForm.controls.rsaSimulationComment.value,
        parentsOnRsa: this.personalDataForm.controls.parentsOnRsa.value,
        taxSituation: this.personalDataForm.controls.taxSituation.value,
        taxDetachment: this.personalDataForm.controls.taxDetachment.value,
        breakupComment: this.personalDataForm.controls.breakupComment.value,
        familyQuotient: this.personalDataForm.controls.familyQuotient.value ? +this.personalDataForm.controls.familyQuotient.value : null,
        recipientNumber: this.personalDataForm.controls.recipientNumber.value ? this.personalDataForm.controls.recipientNumber.value : null,
      } as RsjInstructionPersonalDataDto;
    }
    return this.rsjPersonalData;
  }

  public syncForm(syncPersonalData: RsjInstructionPersonalDataDto): void {
    this.updated = true;
    this.personalDataForm.controls.maritalStatus.setValue(syncPersonalData.maritalStatus);
    this.personalDataForm.controls.dependentsNumber.setValue(syncPersonalData.dependentsNumber);
    this.personalDataForm.updateValueAndValidity();
  }

  private initForm(): void {
    this.personalDataForm = this.formBuilder.group({
      underSupervision: [this.rsjPersonalData?.underSupervision, [Validators.required]],
      supervisionDocument1: [this.rsjPersonalData?.supervisionDocument1, [Validators.required]],
      supervisionDocument2: [this.rsjPersonalData?.supervisionDocument2],
      maritalStatus: [{ value: this.rsjPersonalData?.maritalStatus, disabled: true }],
      partnerOnRsa: [this.rsjPersonalData?.partnerOnRsa],
      dependentsNumber: [{ value: this.rsjPersonalData?.dependentsNumber, disabled: true }],
      pregnancy: [this.rsjPersonalData?.pregnancy],
      rsaSimulation: [this.rsjPersonalData?.rsaSimulation],
      rsaSimulationComment: [this.rsjPersonalData?.rsaSimulationComment],
      parentsOnRsa: [this.rsjPersonalData?.parentsOnRsa],
      taxSituation: [this.rsjPersonalData?.taxSituation, [Validators.required]],
      taxDetachment: [this.rsjPersonalData?.taxDetachment],
      breakupComment: [this.rsjPersonalData?.breakupComment, [Validators.required]],
      familyQuotient: [this.rsjPersonalData?.familyQuotient, [Validators.required]],
      recipientNumber: [this.rsjPersonalData?.recipientNumber],
    });
    this.updateSupervisionVisibility();
    this.updatePartnerOnRsaVisibility();
    this.updateRsaSimulationVisibility();
    this.updateTaxSituationVisibility();
    if (this.disabled) {
      this.personalDataForm.disable({ emitEvent: false });
    }
  }

  private initValueChangeHandlers(): void {
    this.personalDataForm.controls.underSupervision.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSupervisionVisibility());
    this.personalDataForm.controls.maritalStatus.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePartnerOnRsaVisibility());
    this.personalDataForm.controls.dependentsNumber.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateRsaSimulationVisibility());
    this.personalDataForm.controls.pregnancy.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateRsaSimulationVisibility());
    this.personalDataForm.controls.taxSituation.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateTaxSituationVisibility());
    this.personalDataForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
  }

  private updateSupervisionVisibility(): void {
    if (this.personalDataForm.controls.underSupervision.value) {
      this.personalDataForm.controls.supervisionDocument1.enable();
      this.personalDataForm.controls.supervisionDocument2.enable();
    } else {
      this.personalDataForm.controls.supervisionDocument1.reset();
      this.personalDataForm.controls.supervisionDocument1.disable();
      this.personalDataForm.controls.supervisionDocument2.reset();
      this.personalDataForm.controls.supervisionDocument2.disable();
    }
  }

  private updatePartnerOnRsaVisibility(): void {
    this.showPartnerOnRsa =
      this.personalDataForm.controls.maritalStatus.value &&
      [MaritalStatus.Married, MaritalStatus.PACS, MaritalStatus.DeFactoSeparated, MaritalStatus.MarriedLife].includes(
        MaritalStatus[this.personalDataForm.controls.maritalStatus.value],
      );
    if (this.showPartnerOnRsa) {
      this.personalDataForm.controls.partnerOnRsa.enable();
    } else {
      this.personalDataForm.controls.partnerOnRsa.reset();
      this.personalDataForm.controls.partnerOnRsa.disable();
    }
  }

  private updateRsaSimulationVisibility(): void {
    if (this.personalDataForm.controls.dependentsNumber.value > 0 || this.personalDataForm.controls.pregnancy.value) {
      this.personalDataForm.controls.rsaSimulation.enable();
      this.personalDataForm.controls.rsaSimulationComment.enable();
    } else {
      this.personalDataForm.controls.rsaSimulation.reset();
      this.personalDataForm.controls.rsaSimulation.disable();
      this.personalDataForm.controls.rsaSimulationComment.reset();
      this.personalDataForm.controls.rsaSimulationComment.disable();
    }
  }
  public updateRecipientNumber(): void {
    if (
      this.personalDataForm.controls.taxSituation.value === TaxSituation.NoSupportFromPartner &&
      this.beneficiary?.recipientNumber &&
      !this.rsjPersonalData?.recipientNumber
    ) {
      this.personalDataForm.controls.recipientNumber.setValue(this.beneficiary.recipientNumber);
    } else if (this.personalDataForm.controls.recipientNumber.value && !this.rsjPersonalData?.recipientNumber) {
      this.personalDataForm.controls.recipientNumber.setValue(null);
    }
  }

  private updateTaxSituationVisibility(): void {
    this.updateRecipientNumber();
    if (this.personalDataForm.controls.taxSituation.value === TaxSituation.DetachedFromParents) {
      this.taxSituationCase = 1;
      this.personalDataForm.controls.taxDetachment.enable();
      this.personalDataForm.controls.breakupComment.disable();
      this.personalDataForm.controls.breakupComment.reset();
      this.personalDataForm.controls.familyQuotient.disable();
      this.personalDataForm.controls.familyQuotient.reset();
    } else if (this.personalDataForm.controls.taxSituation.value === TaxSituation.FamilyBreakdown) {
      this.taxSituationCase = 2;
      this.personalDataForm.controls.taxDetachment.reset();
      this.personalDataForm.controls.taxDetachment.disable();
      this.personalDataForm.controls.breakupComment.enable();
      this.personalDataForm.controls.familyQuotient.reset();
      this.personalDataForm.controls.familyQuotient.disable();
    } else if (
      [TaxSituation.NoSupportFromParents, TaxSituation.NoSupportFromPartner, TaxSituation.ParentsWithLimitedQuotient].includes(
        this.personalDataForm.controls.taxSituation.value,
      )
    ) {
      this.taxSituationCase = 3;
      this.personalDataForm.controls.taxDetachment.reset();
      this.personalDataForm.controls.taxDetachment.disable();
      this.personalDataForm.controls.breakupComment.reset();
      this.personalDataForm.controls.breakupComment.disable();
      this.personalDataForm.controls.familyQuotient.enable();
    } else {
      this.taxSituationCase = undefined;
      this.personalDataForm.controls.taxDetachment.reset();
      this.personalDataForm.controls.taxDetachment.disable();
      this.personalDataForm.controls.breakupComment.reset();
      this.personalDataForm.controls.breakupComment.disable();
      this.personalDataForm.controls.familyQuotient.reset();
      this.personalDataForm.controls.familyQuotient.disable();
    }
  }
}
